/**
 * JobTitlesInput — Smart tag input for multiple job titles.
 *
 * Features:
 *  - Shows existing titles as removable chips/tags
 *  - Fetches existing job titles from the server and shows them as suggestions
 *  - Allows typing a new title not in the list (add with Enter or "+" button)
 *  - Autocomplete dropdown filters as you type
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import { ApiClient } from '../api/ApiClient';
import styles from './JobTitlesInput.module.css';

interface Props {
  value: string[];
  onChange: (titles: string[]) => void;
  disabled?: boolean;
  error?: string;
}

export default function JobTitlesInput({ value, onChange, disabled, error }: Props): JSX.Element {
  const [inputText, setInputText] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [allTitles, setAllTitles] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch all existing job titles on mount
  useEffect(() => {
    let cancelled = false;
    ApiClient.getJobTitles()
      .then(({ titles }) => {
        if (!cancelled) setAllTitles(titles);
      })
      .catch(() => {/* non-critical */})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Filter suggestions based on input text
  useEffect(() => {
    const text = inputText.trim().toLowerCase();
    if (text.length === 0) {
      setSuggestions(allTitles.filter(t => !value.includes(t)));
    } else {
      setSuggestions(
        allTitles.filter(
          t => t.toLowerCase().includes(text) && !value.includes(t),
        ),
      );
    }
  }, [inputText, allTitles, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addTitle = useCallback((title: string) => {
    const trimmed = title.trim();
    if (!trimmed || value.includes(trimmed) || trimmed.length < 2) return;
    onChange([...value, trimmed]);
    setInputText('');
    setShowDropdown(false);
    inputRef.current?.focus();
  }, [value, onChange]);

  const removeTitle = useCallback((title: string) => {
    onChange(value.filter(t => t !== title));
  }, [value, onChange]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTitle(inputText);
    } else if (e.key === 'Backspace' && inputText === '' && value.length > 0) {
      removeTitle(value[value.length - 1]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  }, [inputText, addTitle, removeTitle, value]);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
    setShowDropdown(true);
  }, []);

  const filteredSuggestionsToShow = inputText.trim().length === 0
    ? allTitles.filter(t => !value.includes(t)).slice(0, 8)
    : suggestions.slice(0, 8);

  const canAddNew = inputText.trim().length >= 2 && !value.includes(inputText.trim())
    && !allTitles.some(t => t.toLowerCase() === inputText.trim().toLowerCase());

  return (
    <div className={styles.container} ref={containerRef}>
      {/* Selected tags */}
      <div
        className={`${styles.tagsBox} ${error ? styles.tagsBoxError : ''}`}
        onClick={() => { inputRef.current?.focus(); setShowDropdown(true); }}
        role="group"
        aria-label="المسميات الوظيفية المختارة"
      >
        {value.map(title => (
          <span key={title} className={styles.tag}>
            <span className={styles.tagText}>{title}</span>
            {!disabled && (
              <button
                type="button"
                className={styles.tagRemove}
                onClick={(e) => { e.stopPropagation(); removeTitle(title); }}
                aria-label={`إزالة ${title}`}
              >
                ✕
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          className={styles.tagInput}
          type="text"
          value={inputText}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowDropdown(true)}
          placeholder={value.length === 0 ? 'اكتب أو اختر مسمى وظيفي...' : 'أضف مسمى آخر...'}
          disabled={disabled}
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
          autoComplete="off"
        />
      </div>

      {/* Dropdown */}
      {showDropdown && !disabled && (
        <div className={styles.dropdown} role="listbox">
          {loading && (
            <div className={styles.dropdownLoading}>جاري التحميل...</div>
          )}

          {/* Existing titles */}
          {filteredSuggestionsToShow.length > 0 && (
            <>
              {inputText.trim().length === 0 && (
                <div className={styles.dropdownLabel}>المسميات الموجودة</div>
              )}
              {filteredSuggestionsToShow.map(title => (
                <button
                  key={title}
                  type="button"
                  className={styles.dropdownItem}
                  role="option"
                  aria-selected={false}
                  onMouseDown={(e) => { e.preventDefault(); addTitle(title); }}
                >
                  <span className={styles.dropdownItemIcon}>+</span>
                  {title}
                </button>
              ))}
            </>
          )}

          {/* Add new option */}
          {canAddNew && (
            <button
              type="button"
              className={`${styles.dropdownItem} ${styles.dropdownItemNew}`}
              role="option"
              aria-selected={false}
              onMouseDown={(e) => { e.preventDefault(); addTitle(inputText); }}
            >
              <span className={styles.dropdownItemIcon}>✨</span>
              إضافة "{inputText.trim()}" كمسمى جديد
            </button>
          )}

          {!loading && filteredSuggestionsToShow.length === 0 && !canAddNew && (
            <div className={styles.dropdownEmpty}>
              {inputText.trim().length > 0 ? 'المسمى قصير جداً (أدنى 2 حرف)' : 'لا توجد مسميات بعد'}
            </div>
          )}
        </div>
      )}

      {/* Hint */}
      <p className={styles.hint}>
        اضغط Enter أو الفاصلة لإضافة مسمى • اضغط Backspace لحذف الأخير
      </p>
    </div>
  );
}
